import React, { useContext, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import UserContext from '../../pages/Login/UserContext';
import { createUser, signUpGuestUser, login, mailResetPassword } from '../../graphql/mutations';
import './RegisterCard.scss';
import { track } from '../../utils/mixpanel';
import { mixpanelIdentifyUser } from '../../utils/mixpanelIdentifyUser';

const RegisterCard = ({ onSuccess, isRemoveShadow = false, isTitle = false, isTitleInsideBox = false }) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext) || {};
  const { user, saveUserToLocalStorage, setAuthData } = userContext;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('register'); // 'register' | 'login' | 'forgot'
  const [error, setError] = useState('');
  const [emailFocusedTracked, setEmailFocusedTracked] = useState(false);
  const [passwordFocusedTracked, setPasswordFocusedTracked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const [signUpGuestUserMutation] = useMutation(gql(signUpGuestUser));
  const [signUpNewUserMutation] = useMutation(gql(createUser));
  const [loginMutation] = useMutation(gql(login));
  const [mailResetPasswordMutation] = useMutation(gql(mailResetPassword));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      if (mode === 'register') {
        try { track('APP_Felt_Register_Submit_Attempt', { path: window.location.pathname }); } catch {}
        const mutation = user?.isGuest ? signUpGuestUserMutation : signUpNewUserMutation;
        const resultKey = user?.isGuest ? 'signUpGuestUser' : 'createUser';
        const result = await mutation({ variables: { userInput: { email, password } } });
        const resultData = result.data[resultKey];
        const { token, user: createdUser } = resultData;
        if (saveUserToLocalStorage && setAuthData) {
          saveUserToLocalStorage(token, createdUser.id);
          setAuthData(resultData);
        }
        mixpanelIdentifyUser(createdUser);
        try { track('APP_Felt_Register_Success', { path: window.location.pathname }); } catch {}
        onSuccess && onSuccess({ email });
      } else if (mode === 'login') {
        try { track('APP_Felt_Login_Submit_Attempt', { path: window.location.pathname }); } catch {}
        const result = await loginMutation({ variables: { email, password } });
        const resultData = result?.data?.login;
        if (resultData && saveUserToLocalStorage && setAuthData) {
          saveUserToLocalStorage(resultData.token, resultData.id);
          setAuthData(resultData);
        }
        if (resultData?.user) {
          mixpanelIdentifyUser(resultData.user);
        }
        try { track('APP_Felt_Login_Success'); } catch {}
        onSuccess && onSuccess({ email });
      } else if (mode === 'forgot') {
        await mailResetPasswordMutation({ variables: { email } });
        try { track('APP_Felt_ForgotPassword_Sent'); } catch {}
        // Switch back to login after sending
        setMode('login');
      }
    } catch (err) {
      const msg = err?.message || 'Error';
      if (isMountedRef.current) setError(msg);
      try { track('APP_Felt_Auth_Error', { message: String(msg).slice(0, 200), mode }); } catch {}
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const renderForgotHint = () => {
    if (mode !== 'login') return null;
    return (
      <div style={{ marginTop: 8, textAlign: 'end' }}>
        <button type="button" className="link_no-button" onClick={() => setMode('forgot')}>
          {t('loginFlow.forgotPassword')}
        </button>
      </div>
    );
  };

  const cardClass = `RegisterCard__card${isRemoveShadow ? ' no-shadow' : ''}`;

  const submitLabel = mode === 'register' ? t('loginFlow.createAnAccount') : mode === 'login' ? t('loginFlow.logInBtn') : t('general.send');
  const titleText = mode === 'login' ? t('loginFlow.logInCatbears') : t('paywall.createAccountTitle');

  const renderModeContent = () => {
    switch (mode) {
      case 'login':
        return (
          <>
            {isTitleInsideBox && (
              <div className="RegisterCard__titleInsideBox">{t('general.loginWithYourAccount')}</div>
            )}
            <label className="RegisterCard__label">{t('loginFlow.email')}</label>
            <input
              type="email"
              className={`RegisterCard__input ${error ? 'has-error' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => { if (!emailFocusedTracked) { try { track('APP_Felt_Register_Email_Focus'); } catch {} ; setEmailFocusedTracked(true); } }}
              required
            />
            <label className="RegisterCard__label">{t('loginFlow.password')}</label>
            <input
              type="password"
              className={`RegisterCard__input ${error ? 'has-error' : ''}`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => { if (!passwordFocusedTracked) { try { track('APP_Felt_Register_Password_Focus'); } catch {} ; setPasswordFocusedTracked(true); } }}
              required
            />
            <div className={`RegisterCard__error ${error ? 'visible' : ''}`}>{error}</div>
            <button type="submit" className="RegisterCard__submit" disabled={isSubmitting}>
              {t('loginFlow.logInBtn')}
            </button>
            <div className="RegisterCard__links">
              <button type="button" className="link_no-button" onClick={() => setMode('forgot')}>
                {t('loginFlow.forgotPassword')}
              </button>
              <div style={{ textAlign: 'center' }}>
                <button type="button" className="link_no-button" onClick={() => setMode('register')}>
                  {t('loginFlow.createAccount')}
                </button>
              </div>
            </div>
          </>
        );
      case 'register':
        return (
          <>
            {isTitleInsideBox && (
              <div className="RegisterCard__titleInsideBox">{t('general.createAFreeAccount')}</div>
            )}
            <label className="RegisterCard__label">{t('loginFlow.email')}</label>
            <input
              type="email"
              className={`RegisterCard__input ${error ? 'has-error' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => { if (!emailFocusedTracked) { try { track('APP_Felt_Register_Email_Focus'); } catch {} ; setEmailFocusedTracked(true); } }}
              required
            />
            <label className="RegisterCard__label">{t('loginFlow.password')}</label>
            <input
              type="password"
              className={`RegisterCard__input ${error ? 'has-error' : ''}`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => { if (!passwordFocusedTracked) { try { track('APP_Felt_Register_Password_Focus'); } catch {} ; setPasswordFocusedTracked(true); } }}
              required
            />
            <div className={`RegisterCard__error ${error ? 'visible' : ''}`}>{error}</div>
            <button type="submit" className="RegisterCard__submit" disabled={isSubmitting}>
              {t('loginFlow.createAnAccount')}
            </button>
            <div className="RegisterCard__links">
              <div className="RegisterCard__terms">
                {t('loginFlow.byCreating') + ' '}
                <a className="link_no-button" href="https://catbears.com/privacy" target="_blank" rel="noopener noreferrer">{t('loginFlow.privacyPolicy')}</a>
                {' ' + t('loginFlow.and')}
                <a className="link_no-button" href="https://catbears.com/terms" target="_blank" rel="noopener noreferrer">{t('loginFlow.TermsOfService')}</a>
                {' ' + t('loginFlow.endOfSentence')}
              </div>
              <div style={{ textAlign: 'center' }}>
                <button type="button" className="link_no-button" onClick={() => setMode('login')}>
                  {t('loginFlow.alreadyAccount')} {t('loginFlow.logIn')}
                </button>
              </div>
            </div>
          </>
        );
      case 'forgot':
        return (
          <>
            {isTitleInsideBox && (
              <div className="RegisterCard__titleInsideBox">{t('general.retrivePassword')}</div>
            )}
            <label className="RegisterCard__label">{t('loginFlow.email')}</label>
            <input
              type="email"
              className={`RegisterCard__input ${error ? 'has-error' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => { if (!emailFocusedTracked) { try { track('APP_Felt_Register_Email_Focus'); } catch {} ; setEmailFocusedTracked(true); } }}
              required
            />
            <div className={`RegisterCard__error ${error ? 'visible' : ''}`}>{error}</div>
            <button type="submit" className="RegisterCard__submit" disabled={isSubmitting}>
              {t('general.send')}
            </button>
            <div className="RegisterCard__links">
              <div style={{ textAlign: 'center' }}>
                <button type="button" className="link_no-button" onClick={() => setMode('login')}>
                  {t('loginFlow.rememberPassword')} {t('loginFlow.logIn')}
                </button>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="RegisterCard__wrapper">
      {isTitle && (
        <div className="RegisterCard__header">
          <div className="RegisterCard__title">{titleText}</div>
        </div>
      )}
      <div className={cardClass}>
        <form onSubmit={handleSubmit} noValidate>
          
          {renderModeContent()}
        </form>
      </div>
    </div>
  );
};

export default RegisterCard;


